import { Injectable, inject } from '@angular/core'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { Deal, DealBase, LocationObject } from '@tradecafe/types/core'
import { DeepPartial, DeepReadonly } from '@tradecafe/types/utils'
import { map, take } from 'rxjs/operators'
import { selectAllLocations } from 'src/app/store/locations'
import { autosaveDealCost, massDealForm, massDealFormFailure, massDealFormSuccess } from 'src/app/store/deal-view.actions'
import { DealFormDto } from 'src/pages/admin/trading/deal-form/deal-form-page/deal-form.schema'
import { CostFormService } from 'src/pages/admin/trading/deal-form/deal-form-page/deal-details/cost-form/cost-form.service'
import { ShipmentRatePickerService } from 'src/components/shipment-rate-picker/shipment-rate-picker.service'
import { ConfirmModalService } from 'src/components/confirm/confirm-modal.service'
import { LocationSelectFormService } from 'src/components/matched-offers/forms/location-select-form/location-select-form.service'
import { DealsService } from 'src/services/data/deals.service'
import { ToasterService } from 'src/shared/toaster/toaster.service'
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty'

function buildDestLocationPatch(deal: DeepReadonly<DealBase>, destLocationId: string): DeepPartial<DealFormDto> {
  return {
    details: { deal_id: deal.deal_id, destLocationId },
    products: deal.offers.map((offer_id, i) => ({
      offer_id,
      bid_id: deal.bids[i],
      buyerIncotermLocationId: destLocationId,
    })),
  }
}

@Injectable({ providedIn: 'root' })
export class DealCloneActionsService {
  private readonly store = inject(Store)
  private readonly actions$ = inject(Actions)
  private readonly locationSelect = inject(LocationSelectFormService)
  private readonly toaster = inject(ToasterService)
  private readonly costForm = inject(CostFormService)
  private readonly deals = inject(DealsService)
  private readonly confirmModal = inject(ConfirmModalService)
  private readonly shipmentRatePicker = inject(ShipmentRatePickerService)


  async addTertiaryCost(
    primaryDeal: DeepReadonly<Deal>,
    targetDeals: DeepReadonly<Deal[]>,
  ): Promise<boolean> {
    if (!targetDeals?.length) return false
    const targets = targetDeals
    const picked = await this.costForm.showCreateCost({
      deal_id: primaryDeal.deal_id,
      status: primaryDeal.status,
      supplier_id: primaryDeal.supplier_id,
      buyer_id: primaryDeal.buyer_id,
    })
    if (!picked?.length) return false

    const dvs = await this.deals.getDealViews(targets.map(d => d.deal_id), ['deal', 'costs'])
      .pipe(take(1)).toPromise()
    this.toaster.progress('Saving deals costs...')
    dvs.forEach(dv => this.store.dispatch(autosaveDealCost({
      dv,
      costs: [...dv.costs, ...picked],
      index: picked.map((_, i) => dv.costs.length + i),
    })))
    return true
  }

  async addFreightRates(
    primaryDeal: DeepReadonly<Deal>,
    targetDeals: DeepReadonly<Deal[]>,
  ): Promise<boolean> {
    if (!targetDeals?.length) return false
    const targets = targetDeals
    try {
      await this.confirmModal.show({
        title: 'Add Freight Rates',
        titleIcon: 'fas fa-exclamation-triangle',
        description: 'The freight rates are reflected based on the source and destination. By bulk editing and manually changing the freight rate, you might not reflect the real value.',
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
      })
    } catch {
      return false
    }
    const picked = await this.shipmentRatePicker.addFreightCost({
      originLocationId: primaryDeal.origin_location,
      destLocationId: primaryDeal.dest_location,
      buyerId: primaryDeal.buyer_id,
      weight: primaryDeal.attributes.estimated?.weight,
      dealId: primaryDeal.deal_id,
    })
    if (!picked) return false

    const dvs = await this.deals.getDealViews(targets.map(d => d.deal_id), ['deal', 'costs'])
      .pipe(take(1)).toPromise()
    this.toaster.progress('Saving deals costs...')
    dvs.forEach(dv => this.store.dispatch(autosaveDealCost({
      dv,
      costs: [...dv.costs, picked],
      index: [dv.costs.length],
    })))
    return true
  }

  async changeDeliveryLocation(
    primaryDeal: DeepReadonly<Deal>,
    targetDeals: DeepReadonly<Deal[]>,
  ): Promise<boolean> {
    if (!targetDeals?.length) return false
    const locationIds = await this.store
      .pipe(select(selectAllLocations), waitNotEmpty(), map(ls => ls.map(l => l.location_id)), take(1))
      .toPromise()
    const picked = await this.locationSelect.open(locationIds, {
      title: 'Change Delivery Location',
      actionTitle: 'Update',
    })
    if (!picked) return false
    const destLocationId = typeof picked === 'string' ? picked : (picked as LocationObject).location_id
    if (!destLocationId) return false

    const targets = targetDeals
    const dvs = await this.deals.getDealViews(targets.map(d => d.deal_id), ['deal'])
      .pipe(take(1)).toPromise()
    const changes = dvs.map(dv => ({ dv, patch: buildDestLocationPatch(dv.deal, destLocationId) }))
    return this.applyMassChange(changes)
  }

  protected applyMassChange(changes: Parameters<typeof massDealForm>[0]['changes']): Promise<boolean> {
    if (!changes.length) return Promise.resolve(true)
    const settled = this.actions$.pipe(
      ofType(massDealFormSuccess, massDealFormFailure),
      take(1),
    ).toPromise().then(action => action.type === massDealFormSuccess.type)
    this.store.dispatch(massDealForm({ changes }))
    return settled
  }
}
